import { Component } from '@angular/core';
import {
  FormGroup,
  FormControl,
  Validators,
  ReactiveFormsModule,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { IbanFieldComponent } from 'src/app/shared/components/iban-field/iban-field.component';
import { InsuranceService } from 'src/app/core/services/insurance.service';
import { IPolicyRequest } from '@interfaces/policy-request.interface';
import { Router } from '@angular/router';
import { StatusService } from 'src/app/core/services/status.service';
import { AgeValidator } from 'src/app/core/validators/underage.validator';
import { NgIf, NgClass, NgFor } from '@angular/common';
import { Observable, map, catchError, of } from 'rxjs';
import { ITarifa } from '@interfaces/tarifa.interface';


@Component({
  selector: 'aig-contact-form',
  imports: [ReactiveFormsModule, IbanFieldComponent, NgIf, NgClass, NgFor],
  templateUrl: './contact-form.component.html',
  styleUrls: ['./contact-form.component.scss'],
  standalone: true,
})
export class ContactFormComponent {
  //#region VARIABLES
  public IdTipoPoliza: number = 1;
  public contactForm: FormGroup = new FormGroup({});
  public registeredDni: boolean = false;
  public aplica_promocion: boolean = false;
  public ibanMandatory: boolean = true;
  public precioFinal: number = 17.77;
  tarifas?: ITarifa;
  descriptions: { title: string; value: string }[] = [
    { title: 'Prima neta anual', value: '16,44€' },
    { title: 'IPS 8%', value: '1,31€' },
    { title: 'LEA 0,15%', value: '0,02€' },
    { title: 'Prima total anual', value: '17,77€' },
  ];
  //#endregion

  //#region CONSTRUCTOR & LIFE-CYCLE HOOKS
  constructor(
    private insuranceService: InsuranceService,
    private router: Router,
    private statusService: StatusService
  ) {
    this.buildForm();

    this.obtenerTarifas();

  }
  //#endregion

  //#region PRIVATE METHODS
  private buildForm(): void {
    this.contactForm = new FormGroup({
      nombre: new FormControl('', Validators.required),
      apellidos: new FormControl('', Validators.required),
      // prefijo: new FormControl('+34'),
      telefono: new FormControl('+34', Validators.required),
      email: new FormControl('', [Validators.required, Validators.email]),
      nacionalidad: new FormControl('', [Validators.required]),
      dni: new FormControl('', Validators.required, [
        this.dniAsyncValidator.bind(this),
      ]),
      nacimiento: new FormControl('', [Validators.required, AgeValidator]),
      direccion: new FormControl('', Validators.required),
      bloque: new FormControl(),
      piso: new FormControl(),
      puerta: new FormControl(),
      provincia: new FormControl('', Validators.required),
      cp: new FormControl('', [Validators.required, Validators.max(99999)]),
      poblacion: new FormControl('', Validators.required),
      pais: new FormControl('', Validators.required),
      aceptar: new FormControl(false, Validators.requiredTrue),
      aceptar2: new FormControl(false, Validators.requiredTrue),
      aceptar3: new FormControl(false, Validators.requiredTrue),
      iban1: new FormControl('', Validators.required),
      iban2: new FormControl('', Validators.required),
      iban3: new FormControl('', Validators.required),
      iban4: new FormControl('', Validators.required),
      iban5: new FormControl('', Validators.required),
      iban: new FormControl('', Validators.required), // Nuevo campo
    });
  }
  //#endregion

  //#region PUBLIC METHODS
  public sendData(): void {
    if (this.contactForm.valid) {
      const datos: IPolicyRequest = {
        nombre: this.contactForm.value.nombre,
        apellidos: this.contactForm.value.apellidos,
        email: this.contactForm.value.email,
        nacionalidad: this.contactForm.value.nacionalidad,
        num_Documento: this.contactForm.value.dni,
        prefijo: '+34',
        telefono: this.contactForm.value.telefono,
        fecha_Nacimiento: this.contactForm.value.nacimiento,
        direccion: this.contactForm.value.direccion,
        bloque: this.contactForm.value.bloque,
        piso: this.contactForm.value.piso,
        puerta: this.contactForm.value.puerta,
        poblacion: this.contactForm.value.poblacion,
        provincia: this.contactForm.value.provincia,
        codigo_postal: this.contactForm.value.cp.toString(),
        pais: this.contactForm.value.pais,
        importe: this.precioFinal,
        confirmacion: this.contactForm.value.aceptar,
        consentimiento: this.contactForm.value.aceptar3,
        consentimiento_Cobro: this.contactForm.value.aceptar2,
        iban: this.contactForm.value.iban, // Incluye el país
        aplicaPromocion: this.aplica_promocion,
        ibanModificado: true,
      };

      this.insuranceService.hireInsurance(datos).subscribe({
        next: (response) => {
          this.statusService.setStatus('ok'); // ✅ Éxito
          this.router.navigateByUrl(`/contratacion-poliza/confirmacion`);
        },
        error: (error) => {
          this.statusService.setStatus('ko'); // ❌ Error
          this.router.navigateByUrl(`/contratacion-poliza/confirmacion`);
        },
      });
    }
  }

  public updateIban(iban: string): void {
    this.contactForm.patchValue({ iban });
  }
  //#endregion

  dniAsyncValidator(
    control: AbstractControl
  ): Observable<ValidationErrors | null> {
    const dni = control.value;

    return this.insuranceService.validarDni(dni).pipe(
      map((res) => {

        // Asignar el valor de registeredDni según res.existe
        this.registeredDni = res.existe === 1; // Si res.existe es 1, se asigna true; si es 0, se asigna false
        this.aplica_promocion =
          res.aplicaPromocion === 1 || res.aplicaPromocion === null; // `true` si es 1 o null, `false` si es 0

        if (this.tarifas) {
          this.descriptions = this.mapTarifasToDescriptions(this.tarifas, this.aplica_promocion)
        }

        if (res.existe === 1) {
          return { dniNoValido: true }; // Si el DNI es válido, el validador pasa sin errores
        } else {
          return null; // Si el DNI no es válido, devolvemos un error
        }
      }),
      catchError(() => {
        // Si ocurre un error, permitimos el valor (también puedes manejar el error aquí si lo deseas)
        return of(null);
      })
    );
  }

  obtenerTarifas() {
    this.insuranceService.obtenerTarifas(this.IdTipoPoliza).subscribe({
      next: (data) => {
        this.tarifas = data;
        this.descriptions = this.mapTarifasToDescriptions(this.tarifas, false);
      },
      error: (err) => {
        console.error(err);
      },
    });
  }

  private mapTarifasToDescriptions(
    tarifa: ITarifa, aplicaPromocion : boolean
  ): { title: string; value: string }[] {
    if (aplicaPromocion){
      if (tarifa.primaBrutaPromocional)
        this.precioFinal = tarifa.primaBrutaPromocional;

      return [
        { title: 'Prima neta anual', value: `${tarifa.primaNetaPromocional?.toLocaleString('es-Es', { minimumFractionDigits: 2 })}€` },
        { title: 'IPS 8%', value: `${tarifa.ipsPromocional?.toLocaleString('es-Es', { minimumFractionDigits: 2 })}€` },
        { title: 'LEA 0,15%', value: `${tarifa.leaPromocional?.toLocaleString('es-Es', { minimumFractionDigits: 2 })}€` },
        { title: 'Prima total anual', value: `${tarifa.primaBrutaPromocional?.toLocaleString('es-Es', { minimumFractionDigits: 2 })}€` },
      ];
    }else {
      if (tarifa.primaBrutaAnual)
        this.precioFinal = tarifa.primaBrutaAnual;

      return [
        { title: 'Prima neta anual', value: `${tarifa.primaNetaAnual?.toLocaleString('es-Es', { minimumFractionDigits: 2 })}€` },
        { title: 'IPS 8%', value: `${tarifa.ips?.toLocaleString('es-Es', { minimumFractionDigits: 2 })}€` },
        { title: 'LEA 0,15%', value: `${tarifa.lea?.toLocaleString('es-Es', { minimumFractionDigits: 2 })}€` },
        { title: 'Prima total anual', value: `${tarifa.primaBrutaAnual?.toLocaleString('es-Es', { minimumFractionDigits: 2 })}€` },
      ];}
  }
}
